/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { ComponentProps, FC } from "react";
import { useDispatch } from "react-redux";
import * as _ from "lodash";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { GamePlanEntity, GamePlanList, GamePlans, TodoItemEntity, TodoItemStatus } from "@ctra/api";
import { makeTrackEvent } from "@ctra/analytics";

import {
  CtraLayout,
  Empty,
  PageHeader,
  Row,
  Skeleton,
  GamePlan,
  Typography,
  WithSuperScript,
  EmptyTask
} from "@ctra/components";

import { UserProvider } from "@auth";
import { UserListProvider } from "@base";
import { useFarmList, useFarm } from "@farms";

import { GamePlanListProvider, TodoListProvider } from "../../providers";
import { GACategories, GAActions } from "../../analytics";
import { useTaskUpdateCycle } from "../../hooks";
import { TaskEntryExtras } from "../TaskEntryExtras";
import styles from "./TaskList.module.less";

const { Paragraph } = Typography;
const { AsyncLayout } = CtraLayout;

const {
  V1: { TaskList: TaskListUI, TaskVariant }
} = GamePlan;

/**
 * List of tasks assigned to the current user
 * @constructor
 */
export const TaskList: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { farm } = useFarm();
  const { updatingTasks, setUpdatingTask } = useTaskUpdateCycle();
  const trackEvent = makeTrackEvent(GACategories.tasks);
  const { farmList, isLoading: isFarmListLoading } = useFarmList();

  const {
    layouts: { tasks },
    gamePlan: {
      todo: {
        list: { empty }
      }
    }
  } = Content;

  /**
   * Render assign user button
   * @param gamePlanList
   * @param task
   */
  const renderExtras = (gamePlanList: GamePlanList, task: TodoItemEntity) => <TaskEntryExtras task={task} />;

  /**
   * Handle updating tasks
   * @param meta
   * @param rest
   * @param action
   */
  const onUpdateTask: ComponentProps<typeof TaskListUI>["onChange"] = ({ meta, ...rest }, action) => {
    /* istanbul ignore next */
    trackEvent(rest?.status ? GAActions.unmarkTask : GAActions.markTask);
    dispatch(GamePlans.actions.updateTask.start(meta?.gamePlanID as GamePlanEntity["id"], rest, action));
    setUpdatingTask(rest);
  };

  /**
   * No tasks in list
   * @todo add to a new component
   */
  const emptyState = (
    <Row align="middle" justify="center" className={styles.Empty}>
      <Empty image={<EmptyTask />} description={null}>
        <Paragraph strong>{t<string>(empty.title)}</Paragraph>
        <Paragraph>{t<string>(empty.description)}</Paragraph>
      </Empty>
    </Row>
  );

  return (
    <UserListProvider>
      {(users, { isLoading: isUserListLoading }) => (
        <UserProvider>
          {(user, { isLoading: isUserLoading }) => (
            <GamePlanListProvider farmID={farm?.id}>
              {(gamePlanList, { isLoading: isGamePlanListLoading }) => (
                <TodoListProvider status={TodoItemStatus.ToDo} farmID={farm?.id} userID={user.id}>
                  {(taskList) => {
                    /**
                     * Prepare task list items
                     */
                    const taskItems = _.map(taskList, ({ meta, ...task }) => ({
                      meta,
                      /**
                       * Conditionally append either game plan name or farm name
                       */
                      ...(farm
                        ? { gamePlan: gamePlanList[meta!.gamePlanID].title }
                        : { farm: _.get(farmList, [meta!.farmID, "name"]) }),
                      ...task
                    }));

                    /**
                     * Tell whether the UI is loading
                     */
                    const isLoading =
                      isFarmListLoading || isUserListLoading || isUserLoading || isGamePlanListLoading;

                    /**
                     * Popover with question mark
                     */
                    const pageHeader = (
                      <WithSuperScript
                        content={t<string>(tasks.popover.content)}
                        onMouseEnter={() => {
                          /* istanbul ignore next */
                          trackEvent(GAActions.hoverHelp);
                        }}
                      >
                        {t<string>(tasks.header, { farmName: farm ? farm.name : null })}
                      </WithSuperScript>
                    );

                    return (
                      <AsyncLayout
                        className={styles.TaskList}
                        loading={isLoading}
                        tip={t<string>(tasks.loading, { farmName: farm ? farm.name : null })}
                      >
                        <PageHeader title={pageHeader} />
                        <Skeleton active loading={isLoading} className={styles.Skeleton}>
                          <TaskListUI
                            items={taskItems}
                            variant={TaskVariant.Expanded}
                            loading={!!updatingTasks.length}
                            onChange={onUpdateTask}
                            extra={_.partial(renderExtras, gamePlanList)}
                            empty={emptyState}
                            withPagination
                          />
                        </Skeleton>
                      </AsyncLayout>
                    );
                  }}
                </TodoListProvider>
              )}
            </GamePlanListProvider>
          )}
        </UserProvider>
      )}
    </UserListProvider>
  );
};
